import React, {
    useCallback,
    Fragment,
    memo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import { useDropzone } from 'react-dropzone';
import { withTranslation } from 'react-i18next';

import { resolveFiles } from '../../service/resolveFiles';
import SelectedFilesList from '../SelectedFilesList';
import getValidationError from '../../service/getValidationError';

const DropZone = ({
    input,
    label,
    notice,
    t,
    maxFileSizeMB,
    maxTotalFilesSizeMB,
    maxTotalFilesCount,
    columnGridCount,
    contentBase64Encoded,
    allowedMimeTypes,
    title,
    meta: { touched, error },
}) => {
    const { name, value, onChange } = input;
    const [validationError, setValidationError] = useState('');
    let files = value || [];
    const showErrors = touched && typeof error !== 'undefined';

    const onDrop = useCallback(async (filesToUpload) => {
        const resolvedFilesToUpload = await resolveFiles(
            filesToUpload,
            maxFileSizeMB,
            contentBase64Encoded,
            allowedMimeTypes,
        );
        const totalFiles = [...files, ...resolvedFilesToUpload];
        const validationMessage = getValidationError(
            totalFiles,
            maxTotalFilesCount,
            maxTotalFilesSizeMB,
            allowedMimeTypes,
        );

        if (validationMessage !== null) {
            setValidationError(validationMessage);
        } else {
            setValidationError('');
            files = totalFiles;
            onChange(files);
        }
    }, [files]);

    const onDelete = useCallback(async (id) => {
        files = files.filter(file => file.getId() !== id);
        onChange(files);
    }, [files]);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
    } = useDropzone({ onDrop });

    const {
        onFocus,
        onClick,
        onKeyDown,
        ...rootProps
    } = getRootProps();

    return (
        <Fragment>
            {label && <label>{label}:</label>}
            <div className="drop-zone">
                <div {...rootProps} className={`drop-zone-container ${isDragActive ? 'drag-active' : ''}`}>
                    <input {...getInputProps()} name={name} />
                    <i className="icon-upload-cloud upload-icon" aria-hidden="true" />
                    <h2 className="drop-zone-title text-center">
                        {title || t('theme.dropzone.drop-files-here')}
                    </h2>
                    <span>
                        {t('theme.dropzone.or')}
                    </span>
                    <div
                        className="select-file"
                        role="button"
                        tabIndex={0}
                        onClick={onClick}
                        onKeyDown={onKeyDown}
                        onFocus={onFocus}
                    >
                        {t('theme.dropzone.select_file')}
                    </div>
                </div>
            </div>
            <small>{notice}</small>
            {showErrors && <p className="text-danger">{error}</p>}
            {validationError && <p className="text-danger">{validationError}</p>}
            {files.length > 0 && (
                <SelectedFilesList
                    files={files}
                    deleteFile={onDelete}
                    columnGridCount={columnGridCount}
                />
            )}
        </Fragment>
    );
};

DropZone.defaultProps = {
    notice: '',
    contentBase64Encoded: true,
    allowedMimeTypes: [],
    maxTotalFilesCount: 0,
    title: null,
};

DropZone.propTypes = {
    input: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.array,
        ]),
        onChange: PropTypes.func,
    }).isRequired,
    label: PropTypes.string,
    notice: PropTypes.string,
    t: PropTypes.func.isRequired,
    maxFileSizeMB: PropTypes.number.isRequired,
    maxTotalFilesSizeMB: PropTypes.number.isRequired,
    maxTotalFilesCount: PropTypes.number,
    columnGridCount: PropTypes.number.isRequired,
    contentBase64Encoded: PropTypes.bool,
    allowedMimeTypes: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    meta: PropTypes.shape({
        error: PropTypes.string,
        touched: PropTypes.bool,
    }).isRequired,
};

export default compose(
    withTranslation('DropZone'),
    memo,
)(DropZone);
